"use client"

import { usePathname } from "next/navigation"
import { Header } from "@/components/ui/header"
import { Footer } from "@/components/ui/footer"
import { ChatWidget } from "@/components/chat/chat-widget"
import { RouteScrollRestoration } from "@/components/ui/route-scroll-restoration"
import { KonamiGameLauncher } from "@/components/games/konami-game-launcher"

// Chat hidden site-wide 2026-08-08 (owner call — not needed on the site right
// now). Gated at the mount rather than inside ChatWidget on purpose: the
// component keeps its own tests, which render it directly, and /api/ai/chat
// stays reachable. Nothing is deleted; flip to true to restore.
const SHOW_CHAT_WIDGET: boolean = false

interface LayoutWrapperProps {
  children: React.ReactNode
}

function SkipToMainContent() {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:z-100 focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-white focus:text-green-700 focus:rounded-md focus:shadow-lg focus:ring-2 focus:ring-green-500 focus:outline-hidden"
    >
      Skip to main content
    </a>
  )
}

export function LayoutWrapper({ children }: LayoutWrapperProps) {
  const pathname = usePathname()
  const isEmbedRoute = pathname?.startsWith('/embed')
  const isAdminRoute = pathname?.startsWith('/admin')
  const isDashboardRoute = pathname?.startsWith('/dashboard')
  const isAuthRoute = pathname?.startsWith('/auth')
  // The /games index is a normal chrome page; only the full-viewport game
  // canvases under /games/* own their shell.
  const isGameRoute = pathname?.startsWith('/games/')
  const isToolRoute = pathname?.startsWith('/tools')
  // Reading surfaces carry the Mediavine adhesion ad. Mediavine's docs tell
  // every floating-tool vendor the same thing: move off the bottom edge or
  // disable — never stack above the anchor (accidental-click policy risk).
  // Chat usage is ~0.15% of pageviews, so the FAB earns its pixels only on
  // work-mode surfaces (funnel, home); replaced on articles by inline entry
  // points (see obsidian feature note, 2026-07-31 chat-placement decision).
  const isReadingRoute =
    pathname?.startsWith('/blog') ||
    pathname?.startsWith('/guides') ||
    pathname?.startsWith('/weeds') ||
    pathname?.startsWith('/pests') ||
    pathname?.startsWith('/compare') ||
    pathname?.startsWith('/lawn-care') ||
    pathname?.startsWith('/products') ||
    pathname?.startsWith('/lawn-products-guide') ||
    pathname?.startsWith('/grass-atlas') ||
    pathname?.startsWith('/what-kind-of-grass-do-i-have')
  const isHomeRoute = pathname === '/'

  // Auth pages keep minimal chrome, but still share the site-level keyboard
  // skip link. Each auth page owns its single main landmark so Suspense and
  // state-specific screens stay inside the same target.
  if (isAuthRoute) {
    return (
      <>
        <SkipToMainContent />
        {children}
      </>
    )
  }

  // Embed, admin, dashboard, and game routes own their application shell.
  if (isEmbedRoute || isAdminRoute || isDashboardRoute || isGameRoute) {
    return <>{children}</>
  }

  // Main site gets full chrome
  // Use flex column layout to ensure proper vertical stacking and footer positioning
  return (
    <div className={`flex flex-col min-h-screen w-full max-w-full pb-ad-dock ${isHomeRoute ? 'bg-green-50' : ''}`}>
      <SkipToMainContent />
      <RouteScrollRestoration />
      <Header />
      <main id="main-content" className="flex-1 w-full max-w-full" tabIndex={-1}>
        {children}
      </main>
      <Footer />
      {/* Below-the-footer slot for page-level fine print (image attribution).
          Pages portal into this via <PageBottomPortal> — see that component. */}
      <div id="page-bottom-slot" className="w-full" />
      {SHOW_CHAT_WIDGET && !isToolRoute && !isReadingRoute && <ChatWidget />}
      <KonamiGameLauncher />
    </div>
  )
}
